import { defineComponent as _defineComponent } from 'vue'
import { createTextVNode as _createTextVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, createBlock as _createBlock, mergeProps as _mergeProps, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, pushScopeId as _pushScopeId, popScopeId as _popScopeId } from "vue"

const _withScopeId = n => (_pushScopeId("data-v-6e79b60c"),n=n(),_popScopeId(),n)
const _hoisted_1 = { class: "d-flex flex-wrap justify-center ga-2 mb-3" }
const _hoisted_2 = { class: "text-center mb-2" }
const _hoisted_3 = {
  key: 0,
  class: "text-center py-6"
}
const _hoisted_4 = { class: "text-body-2 text-medium-emphasis mb-0" }
const _hoisted_5 = { class: "min-w-0" }
const _hoisted_6 = { class: "font-weight-bold nombre" }
const _hoisted_7 = {
  key: 0,
  class: "text-caption text-medium-emphasis"
}

import DetalleCircuito from "@/components/Circuito/DetalleCircuito.vue";
import ComoSePuntuaCircuito from "@/components/Circuito/ComoSePuntuaCircuito.vue";
import ModalNombreCircuito from "@/components/Circuito/components/ModalNombreCircuito.vue";
import ModalSuccess from "@/components/Commons/ModalSuccess.vue";
import { CircuitoDTO } from "@/interfaces/Circuito";
import { esCircuitoAmonSul } from "@/utils/circuitoAmonSul";
import { crearCircuitoAsync, getCircuitos } from "@/services/CircuitosService";
import { mensajeDelBack } from "@/utils/mensajeDelBack";
import { ref, onMounted, computed, watch } from "vue";

// El circuito desplegado
const TODOS = 0;

export default /*#__PURE__*/_defineComponent({
  __name: 'CircuitosView',
  setup(__props) {

const abierto = ref<number | null>(null);

// Referencia para almacenar los circuitos
const circuitos = ref<CircuitoDTO[]>([]);
const loading = ref<boolean>(false);

// Cada circuito, en un solo año: el de su creación (así la suma de los años da «Todos»). Si el servidor aún no
// la manda, el que lleve en el nombre
const ANIO_ACTUAL = new Date().getFullYear();
const aniosDe = (circuito: CircuitoDTO): number[] => {
  if (circuito.fechaCreacion) return [Number(circuito.fechaCreacion.slice(0, 4))];
  const enElNombre = circuito.nombreCircuito.match(/\b(20\d{2})\b/);
  return enElNombre ? [Number(enElNombre[1])] : [];
};
const deAnio = (anio: number) => circuitos.value.filter(c => aniosDe(c).includes(anio));
// El actual primero; los demás, del más nuevo al más antiguo
const anios = computed(() => {
  const todos = [...new Set(circuitos.value.flatMap(aniosDe))];
  return todos.sort((a, b) => (a === ANIO_ACTUAL ? -1 : b === ANIO_ACTUAL ? 1 : b - a));
});
// Fichas para el año actual y los dos anteriores (los que haya); el resto, al desplegable
const aniosRecientes = computed(() =>
  anios.value.filter(a => a <= ANIO_ACTUAL && a > ANIO_ACTUAL - 3)
);
const aniosOtros = computed(() => anios.value.filter(a => !aniosRecientes.value.includes(a)));
const anio = ref<number>(TODOS);
const enDesplegable = computed(() => !aniosRecientes.value.includes(anio.value));
const textoAnio = (a: number) => (a === TODOS ? `Todos (${circuitos.value.length})` : `${a} (${deAnio(a).length})`);
// Al cargar, el año actual; si no hay circuitos de este año, el primero que haya
watch(anios, lista => {
  if (anio.value === TODOS || !lista.includes(anio.value)) anio.value = lista[0] ?? TODOS;
});

// Sin tildes ni mayúsculas
const busqueda = ref<string | null>("");
const verComo = ref(false);
const normalizar = (texto?: string | null) =>
  (texto ?? "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase().trim();
// Por año, del más nuevo al más antiguo, y en cada año el Amon Sûl el primero; después, los más nuevos
const primerAnio = (c: CircuitoDTO) => aniosDe(c)[0] ?? 0;
const ordenCircuitos = (a: CircuitoDTO, b: CircuitoDTO) =>
  primerAnio(b) - primerAnio(a) ||
  Number(esCircuitoAmonSul(b.nombreCircuito)) - Number(esCircuitoAmonSul(a.nombreCircuito)) ||
  b.idCircuito - a.idCircuito;

const filtrados = computed(() => {
  const texto = normalizar(busqueda.value);
  return (anio.value === TODOS ? circuitos.value : deAnio(anio.value))
    .filter(c => !texto || normalizar(c.nombreCircuito).includes(texto))
    .sort(ordenCircuitos);
});

// Función para cargar los circuitos
const loadCircuitos = async () => {
  loading.value = true;
  try {
    const response = await getCircuitos();
    // Los más nuevos primero, como al añadir un torneo a un circuito: en el móvil los de este año quedaban al final
    circuitos.value = [...response.data].sort((a: CircuitoDTO, b: CircuitoDTO) => b.idCircuito - a.idCircuito);
  } catch (error) {
    console.error("Error al cargar circuitos:", error);
  } finally {
    loading.value = false;
  }
};


// Crear un circuito: queda a nombre de quien lo crea
const showCrear = ref(false);
const creando = ref(false);
const errorCrear = ref<string | null>(null);
const showSuccess = ref(false);
const mensajeExito = ref("");

const abrirCrear = () => {
  errorCrear.value = null;
  showCrear.value = true;
};

const crear = async (nombre: string) => {
  creando.value = true;
  errorCrear.value = null;
  try {
    await crearCircuitoAsync(nombre);
    showCrear.value = false;
    await loadCircuitos();
    mensajeExito.value = `Circuito «${nombre}» creado. Los organizadores pueden añadir sus torneos desde la gestión.`;
    showSuccess.value = true;
  } catch (error) {
    errorCrear.value = mensajeDelBack(error, "No se ha podido crear el circuito. Inténtalo de nuevo.");
  } finally {
    creando.value = false;
  }
};

// El creador lo ha renombrado o borrado desde el desplegable
const onRenombrado = async () => {
  await loadCircuitos();
};

const onBorrado = async (nombre: string) => {
  abierto.value = null;
  await loadCircuitos();
  mensajeExito.value = `Circuito «${nombre}» borrado.`;
  showSuccess.value = true;
};

// Cargar circuitos cuando el componente esté montado
onMounted(() => {
  loadCircuitos();
});

return (_ctx: any,_cache: any) => {
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_text_field = _resolveComponent("v-text-field")!
  const _component_v_chip = _resolveComponent("v-chip")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_card = _resolveComponent("v-card")!
  const _component_v_expand_transition = _resolveComponent("v-expand-transition")!
  const _component_v_progress_circular = _resolveComponent("v-progress-circular")!
  const _component_v_expansion_panel_title = _resolveComponent("v-expansion-panel-title")!
  const _component_v_expansion_panel_text = _resolveComponent("v-expansion-panel-text")!
  const _component_v_expansion_panel = _resolveComponent("v-expansion-panel")!
  const _component_v_expansion_panels = _resolveComponent("v-expansion-panels")!
  const _component_v_container = _resolveComponent("v-container")!

  return (_openBlock(), _createBlock(_component_v_container, { class: "circuitos text-left" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_btn, {
        color: "primary",
        variant: "flat",
        class: "mb-3 mx-auto d-flex crear",
        onClick: abrirCrear
      }, {
        default: _withCtx(() => [
          _createTextVNode(" Crear circuito ")
        ]),
        _: 1
      }),
      _createVNode(_component_v_text_field, {
        modelValue: busqueda.value,
        "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((busqueda).value = $event)),
        placeholder: "Buscar circuito",
        "prepend-inner-icon": "mdi-magnify",
        variant: "outlined",
        density: "compact",
        clearable: "",
        "hide-details": "",
        class: "mb-2"
      }, null, 8, ["modelValue"]),
      _createElementVNode("div", _hoisted_1, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(aniosRecientes.value, (a) => {
          return (_openBlock(), _createBlock(_component_v_chip, {
            key: a,
            size: "small",
            color: anio.value === a ? 'primary' : undefined,
            "prepend-icon": anio.value === a ? 'mdi-check' : undefined,
            onClick: ($event: any) => (anio.value = a)
          }, {
            default: _withCtx(() => [
              _createTextVNode(_toDisplayString(a) + " (" + _toDisplayString(deAnio(a).length) + ") ", 1)
            ]),
            _: 2
          }, 1032, ["color", "prepend-icon", "onClick"]))
        }), 128)),
        _createVNode(_component_v_menu, null, {
          activator: _withCtx(({ props: menu }) => [
            _createVNode(_component_v_chip, _mergeProps(menu, {
              size: "small",
              color: enDesplegable.value ? 'primary' : undefined,
              "prepend-icon": enDesplegable.value ? 'mdi-check' : undefined,
              "append-icon": "mdi-menu-down"
            }), {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(enDesplegable.value ? textoAnio(anio.value) : "Más"), 1)
              ]),
              _: 2
            }, 1040, ["color", "prepend-icon"])
          ]),
          default: _withCtx(() => [
            _createVNode(_component_v_list, { density: "compact" }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(aniosOtros.value, (a) => {
                  return (_openBlock(), _createBlock(_component_v_list_item, {
                    key: a,
                    active: anio.value === a,
                    onClick: ($event: any) => (anio.value = a)
                  }, {
                    default: _withCtx(() => [
                      _createTextVNode(_toDisplayString(a) + " (" + _toDisplayString(deAnio(a).length) + ") ", 1)
                    ]),
                    _: 2
                  }, 1032, ["active", "onClick"]))
                }), 128)),
                _createVNode(_component_v_list_item, {
                  active: anio.value === TODOS,
                  onClick: _cache[1] || (_cache[1] = ($event: any) => (anio.value = TODOS))
                }, {
                  default: _withCtx(() => [
                    _createTextVNode("Todos (" + _toDisplayString(circuitos.value.length) + ")", 1)
                  ]),
                  _: 1
                }, 8, ["active"])
              ]),
              _: 1
            })
          ]),
          _: 1
        })
      ]),
      _createElementVNode("div", _hoisted_2, [
        _createVNode(_component_v_btn, {
          variant: "text",
          size: "small",
          color: "primary",
          class: "text-none",
          "prepend-icon": "mdi-information-outline",
          onClick: _cache[2] || (_cache[2] = ($event: any) => (verComo.value = !verComo.value))
        }, {
          default: _withCtx(() => [
            _createTextVNode(" ¿Cómo se puntúa? ")
          ]),
          _: 1
        })
      ]),
      _createVNode(_component_v_expand_transition, null, {
        default: _withCtx(() => [
          (verComo.value)
            ? (_openBlock(), _createBlock(_component_v_card, {
                key: 0,
                variant: "tonal",
                class: "pa-4 mb-3"
              }, {
                default: _withCtx(() => [
                  _createVNode(ComoSePuntuaCircuito)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 1
      }),
      (loading.value)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            _createVNode(_component_v_progress_circular, {
              indeterminate: "",
              color: "primary"
            })
          ]))
        : (filtrados.value.length === 0)
          ? (_openBlock(), _createBlock(_component_v_card, {
              key: 1,
              variant: "outlined",
              class: "pa-4 text-center"
            }, {
              default: _withCtx(() => [
                _createElementVNode("p", _hoisted_4, _toDisplayString(busqueda.value ? "Ningún circuito coincide con la búsqueda." : "No hay circuitos este año."), 1)
              ]),
              _: 1
            }))
          : (_openBlock(), _createBlock(_component_v_expansion_panels, {
              key: 2,
              modelValue: abierto.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((abierto).value = $event)),
              variant: "accordion",
              class: "lista"
            }, {
              default: _withCtx(() => [
                (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(filtrados.value, (circuito) => {
                  return (_openBlock(), _createBlock(_component_v_expansion_panel, {
                    key: circuito.idCircuito,
                    value: circuito.idCircuito,
                    elevation: "0"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_expansion_panel_title, null, {
                        default: _withCtx(() => [
                          _createElementVNode("div", _hoisted_5, [
                            _createElementVNode("div", _hoisted_6, _toDisplayString(circuito.nombreCircuito), 1),
                            (circuito.numeroEventos != null)
                              ? (_openBlock(), _createElementBlock("div", _hoisted_7, _toDisplayString(circuito.numeroEventos) + " " + _toDisplayString(circuito.numeroEventos === 1 ? "evento" : "eventos"), 1))
                              : _createCommentVNode("", true)
                          ])
                        ]),
                        _: 2
                      }, 1024),
                      _createVNode(_component_v_expansion_panel_text, null, {
                        default: _withCtx(() => [
                          _createVNode(DetalleCircuito, {
                            circuito: circuito,
                            onRenombrado: onRenombrado,
                            onBorrado: onBorrado
                          }, null, 8, ["circuito"])
                        ]),
                        _: 2
                      }, 1024)
                    ]),
                    _: 2
                  }, 1032, ["value"]))
                }), 128))
              ]),
              _: 1
            }, 8, ["modelValue"])),
      _createVNode(ModalNombreCircuito, {
        modelValue: showCrear.value,
        "onUpdate:modelValue": _cache[4] || (_cache[4] = ($event: any) => ((showCrear).value = $event)),
        titulo: "Crear circuito",
        textoBoton: "Crear",
        guardando: creando.value,
        error: errorCrear.value,
        onGuardar: crear
      }, null, 8, ["modelValue", "guardando", "error"]),
      _createVNode(ModalSuccess, {
        isVisible: showSuccess.value,
        message: mensajeExito.value,
        "onUpdate:isVisible": _cache[5] || (_cache[5] = ($event: any) => (showSuccess.value = $event))
      }, null, 8, ["isVisible", "message"])
    ]),
    _: 1
  }))
}
}

})